<template>
    <div class="command-palette-overlay" @click="$emit('close')">
        <div class="command-palette" @click.stop>
            <div class="command-palette-header">
                <div class="command-search-container">
                    <i class="material-icons">search</i>
                    <input
                        ref="commandInput"
                        :value="searchQuery"
                        type="text"
                        placeholder="Search for anything..."
                        @input="handleInput"
                        @keydown.down.prevent="$emit('navigate-results', 1)"
                        @keydown.up.prevent="$emit('navigate-results', -1)"
                        @keydown.enter="$emit('select-result')"
                        @keydown.esc="$emit('close')"
                    />
                    <button class="command-palette-shortcut" type="button" @click="$emit('close')">
                        <span>ESC</span>
                    </button>
                </div>
            </div>
            <div class="command-palette-results" v-if="searchQuery">
                <div class="command-palette-section">
                    <div class="command-palette-section-title">
                        <span v-if="filteredResults.pages.length">Pages</span>
                    </div>
                    <button
                        v-for="(page, index) in filteredResults.pages"
                        :key="`page-${index}`"
                        :class="['command-palette-item', { selected: selectedIndex === getItemIndex('pages', index) }]"
                        type="button"
                        @click="$emit('navigate-to', page.path)"
                        @mouseenter="$emit('select-index', getItemIndex('pages', index))"
                    >
                        <i class="material-icons">{{ page.icon }}</i>
                        <span class="item-title">{{ page.title }}</span>
                        <span class="item-description">{{ page.description }}</span>
                    </button>
                </div>

                <div class="command-palette-section" v-if="filteredResults.customers.length">
                    <div class="command-palette-section-title">
                        <span>Customers</span>
                    </div>
                    <button
                        v-for="(customer, index) in filteredResults.customers"
                        :key="`customer-${index}`"
                        :class="['command-palette-item', { selected: selectedIndex === getItemIndex('customers', index) }]"
                        type="button"
                        @click="$emit('navigate-to-customer', customer.id)"
                        @mouseenter="$emit('select-index', getItemIndex('customers', index))"
                    >
                        <i class="material-icons">person</i>
                        <span class="item-title">{{ customer.name }}</span>
                        <span class="item-description">{{ customer.email }}</span>
                    </button>
                </div>

                <div class="command-palette-section" v-if="filteredResults.products.length">
                    <div class="command-palette-section-title">
                        <span>Products</span>
                    </div>
                    <button
                        v-for="(product, index) in filteredResults.products"
                        :key="`product-${index}`"
                        :class="['command-palette-item', { selected: selectedIndex === getItemIndex('products', index) }]"
                        type="button"
                        @click="$emit('navigate-to-product', product.id)"
                        @mouseenter="$emit('select-index', getItemIndex('products', index))"
                    >
                        <i class="material-icons">inventory_2</i>
                        <span class="item-title">{{ product.name }}</span>
                        <span class="item-description">{{ product.sku }}</span>
                    </button>
                </div>

                <div class="command-palette-empty" v-if="isEmptyResults">
                    <i class="material-icons">search_off</i>
                    <span>No results found for "{{ searchQuery }}"</span>
                </div>
            </div>
            <div class="command-palette-sections" v-else>
                <div class="command-palette-section">
                    <div class="command-palette-section-title">
                        <span>Quick Links</span>
                    </div>
                    <button
                        v-for="(page, index) in pages.slice(0, 5)"
                        :key="`quick-${index}`"
                        :class="['command-palette-item', { selected: selectedIndex === index }]"
                        type="button"
                        @click="$emit('navigate-to', page.path)"
                        @mouseenter="$emit('select-index', index)"
                    >
                        <i class="material-icons">{{ page.icon }}</i>
                        <span class="item-title">{{ page.title }}</span>
                        <span class="item-description">{{ page.description }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        searchQuery: {
            type: String,
            default: "",
        },
        filteredResults: {
            type: Object,
            required: true,
        },
        selectedIndex: {
            type: Number,
            default: 0,
        },
        pages: {
            type: Array,
            default: () => [],
        },
        isEmptyResults: {
            type: Boolean,
            default: false,
        },
        getItemIndex: {
            type: Function,
            required: true,
        },
    },
    mounted() {
        this.focusInput()
    },
    methods: {
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.commandInput) {
                    this.$refs.commandInput.focus()
                }
            })
        },
        handleInput(event) {
            this.$emit("update-search-query", event.target.value)
            this.$emit("perform-search")
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "@/assets/variables.scss";

.command-palette-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1400;
    padding-top: 10vh;
}

.command-palette {
    width: 600px;
    background-color: $lightCardBg;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .dark & {
        background: linear-gradient(135deg, $darkCardBg 0%, color.adjust($darkCardBg, $lightness: -2%) 100%);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
        border: 1px solid $darkBorderColor;
    }
}

.command-palette-header {
    padding: 16px;
    border-bottom: 1px solid $lightBorderColor;

    .dark & {
        border-color: $darkBorderColor;
    }
}

.command-search-container {
    display: flex;
    align-items: center;

    i {
        color: $textColor;
        opacity: 0.7;
        font-size: 20px;
        margin-right: 12px;

        .dark & {
            color: $darkTextColor;
        }
    }

    input {
        flex: 1;
        background: none;
        border: none;
        font-size: 16px;
        color: $textColor;

        &:focus {
            outline: none;
        }

        &::placeholder {
            color: $textColor;
            opacity: 0.5;
        }

        .dark & {
            color: $darkTextColor;

            &::placeholder {
                color: $darkTextColor;
                opacity: 0.5;
            }
        }
    }
}

.command-palette-shortcut {
    display: flex;
    align-items: center;
    margin-left: 12px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;

    span {
        background-color: rgba(0, 0, 0, 0.05);
        color: $textColor;
        opacity: 0.8;
        font-size: 12px;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
        border: 1px solid rgba(0, 0, 0, 0.1);

        .dark & {
            background-color: rgba(255, 255, 255, 0.1);
            color: $darkTextColor;
            border-color: rgba(255, 255, 255, 0.1);
        }
    }
}

.command-palette-results {
    max-height: 60vh;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 20px;

        .dark & {
            background-color: rgba(255, 255, 255, 0.2);
        }
    }
}

.command-palette-section {
    padding: 12px 16px;

    .command-palette-section-title {
        font-size: 12px;
        font-weight: 600;
        color: $textColor;
        opacity: 0.6;
        margin-bottom: 8px;

        .dark & {
            color: $darkTextColor;
        }
    }
}

.command-palette-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border-radius: 8px;
    cursor: pointer;
    margin-bottom: 4px;
    width: 100%;
    border: 0;
    background: transparent;
    font: inherit;
    text-align: left;

    i {
        font-size: 20px;
        color: $textColor;
        opacity: 0.7;
    }

    .item-title {
        font-size: 14px;
        font-weight: 500;
        color: $textColor;
        flex: 1;
    }

    .item-description {
        font-size: 13px;
        color: $textColor;
        opacity: 0.6;
    }

    &:hover,
    &.selected {
        background-color: $lightHoverColor;
    }

    .dark & {
        i {
            color: $darkTextColor;
        }

        .item-title {
            color: $darkTextColor;
        }

        .item-description {
            color: $darkTextColor;
        }

        &:hover,
        &.selected {
            background-color: color.adjust($darkCardBg, $lightness: 5%);
        }
    }
}

@media (max-width: 768px) {
    .command-palette-overlay {
        padding: 12px;
        padding-top: 72px;
    }

    .command-palette {
        width: 100%;
        max-width: 600px;
        border-radius: 10px;
    }
}

@media (max-width: 480px) {
    .command-palette-item {
        grid-template-columns: 24px minmax(0, 1fr);

        .item-description {
            display: none;
        }
    }
}
</style>
